import { Injectable } from "@angular/core";
import { CtSerializerHelperService, CtWebapiService, DataRequest } from "@ctsolution/ct-webapi";
import { CtWebapiGenericResponse } from "@ctsolution/ct-webapi/core/response.interface";
import { AccessLinkAuthResponse } from "../interfaces/access-link-auth-response";
import { AccessLinkMockService } from "./access-link.mock.service";
import { environment } from "src/environments/environment";

/**
 * Estensione locale del tipo environment per i flag mock opzionali.
 * Evita errori TypeScript negli environment che non dichiarano le proprietà.
 */
interface EnvironmentWithMock {
  accessLinkMock?: boolean;
  accessLinkMockError?: boolean;
}

@Injectable({
  providedIn: "root"
})
export class AccessLinkController {

  private readonly controller: string = "/AccessLink/";

  /** Flag di mock letto dall'environment — false se non dichiarato */
  private get isMockEnabled(): boolean {
    return (environment as EnvironmentWithMock).accessLinkMock ?? false;
  }

  /** Flag di simulazione errore — false se non dichiarato */
  private get isMockError(): boolean {
    return (environment as EnvironmentWithMock).accessLinkMockError ?? false;
  }

  constructor(
    private _webapi: CtWebapiService,
    private serializerHelperService: CtSerializerHelperService,
    private mockService: AccessLinkMockService
  ) {}

  /**
   * Verifica il token e restituisce la configurazione della pagina AccessLink.
   *
   * In modalità mock (environment.accessLinkMock === true) bypassa il backend.
   * Per simulare un errore 400 impostare anche environment.accessLinkMockError === true.
   *
   * @param token - Token letto dal queryParam
   */
  auth(token: string): Promise<AccessLinkAuthResponse> {

    if (this.isMockEnabled) {
      return this.mockService.auth(this.isMockError);
    }

    return new Promise<AccessLinkAuthResponse>((resolve, reject) => {

      const request: DataRequest = new DataRequest(`${this.controller}Auth`)
      .setQueryParams({ token: token,hideError: true, hideSpinner: true });

      this._webapi
        .get(request)
        .subscribe({
          next: (r: CtWebapiGenericResponse<AccessLinkAuthResponse>) => resolve(r.Result),
          error: (err) => reject(err)
        });

    });

  }

  /**
   * Carica un file per la sessione corrente.
   * Invia multipart/form-data con i campi File e Token.
   *
   * @param file  - File selezionato dall'utente
   * @param token - Token della sessione corrente
   */
  uploadFile(file: File, token: string): Promise<boolean> {

    if (this.isMockEnabled) {
      return this.mockService.uploadFile(file, token);
    }

    return new Promise<boolean>((resolve, reject) => {

      // Costruzione del FormData — stesso pattern usato in flow-steps.controller.ts
      const payload: { file: File;} = { file: file};
      const formData: FormData = this.serializerHelperService.objectToFormData(payload);

      const request: DataRequest = new DataRequest(`${this.controller}UploadFile`)
      .setQueryParams({ token: token });

      this._webapi
        .post(request, formData)
        .subscribe({
          next: () => resolve(true),
          error: (err) => reject(err)
        });

    });

  } 

}
